import React, { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers";
import * as dayjs from "dayjs";
import IntlMessages from "util/IntlMessages";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid
} from "@material-ui/core";
import FormInput from "components/Form/Controls/input";
import FormSelectAutoComplete from "components/Form/Controls/select-autocomplete";
import FormDatePicker from "components/Form/Controls/datepicker";
import ButtonAsync from "components/Form/Controls/ButtonAsync";

import { callApi, getCitiesByStateProvinceId, getAllStateProvince } from "api/apiList";

const QarzibaForm = ({
    rowObj,
    prescriptionId,
    openCRU,
    handleRequestClose,
    handleCreateUpdate,
    readOnly,
    boolList,
    DiagnosticCodeCIEList
}) => {
    const getCboValue = (items, id) => {
        if (typeof id === "object") return id;
        const result = items.find((x) => x.id === id);
        return result ? { label: result.name, value: result.id } : null;
    };

    const defaultValues = {
        cie10_11: null,
        cie10OtherDescription: "",
        relapseDisease: null,
        vialsPerCycle: "",
        totalCyclesFormulated: "",
        totalFormulatedVials: "",
        totalVialsApproved: "",
        totalAppliedVials: "",
        purchasedVials: "",
        purchasedNotApplied: "",
        unusedVials: "",
        importNotAvailable: null,
        submissionDate: null,
        autoDate: null,
        approvalDate: null,
        authorizationNumber: "",
        awardedTo: "",
        startDateTreatment: null
    };

    const validationSchema = yup.object().shape({
        cie10_11: yup.object().nullable(),
        cie10OtherDescription: yup
            .string()
            .when("cie10_11", {
                is: (cie10_11) => cie10_11 && cie10_11.value === 16,
                then: yup
                    .string()
                    .required("Este campo es requerido cuando se selecciona 'OTRO'")
                    .max(200, "Máximo 200 caracteres")
                    .matches(/^[a-zA-Z0-9\sáéíóúÁÉÍÓÚñÑ.,-]*$/),
                otherwise: yup.string().nullable()
            }),
        relapseDisease: yup.object().nullable(),
        vialsPerCycle: yup.number().nullable(),
        totalCyclesFormulated: yup.number().nullable(),
        totalFormulatedVials: yup.number().nullable(),
        totalVialsApproved: yup.number().nullable(),
        totalAppliedVials: yup.number().nullable(),
        purchasedVials: yup.number().nullable(),
        purchasedNotApplied: yup.number().nullable(),
        unusedVials: yup.number().nullable(),
        importNotAvailable: yup.object().nullable(),
        submissionDate: yup.date().nullable(),
        autoDate: yup.date().nullable(),
        approvalDate: yup.date().nullable(),
        authorizationNumber: yup.string().matches(/^[0-9]*$/, "Solo números").max(50, "Máximo 50 caracteres"),
        awardedTo: yup.string().nullable(),
        startDateTreatment: yup.date().nullable()
    });

    const methods = useForm({
        defaultValues,
        resolver: yupResolver(validationSchema)
    });

    const { handleSubmit, setValue, errors, watch } = methods;

    const cie10_11Value = watch("cie10_11");

    useEffect(() => {
        if (openCRU) {
            setTimeout(() => loadData(rowObj), 150);
        }
    }, [openCRU]);

    useEffect(() => {
        if (cie10_11Value && cie10_11Value.value !== 16) {
            setValue("cie10OtherDescription", "");
        }
    }, [cie10_11Value, setValue]);

    const loadData = (row) => {
        if (!row) return;
        console.log('');
        setValue("cie10_11", row.cie10_11 ? getCboValue(DiagnosticCodeCIEList, row.cie10_11) : null);
        setValue("cie10OtherDescription", row.cie10OtherDescription ?? "");
        setValue(
            "relapseDisease",
            row.relapseDisease !== null
                ? { label: row.relapseDisease ? "Sí" : "No", value: row.relapseDisease ? 1 : 0 }
                : null
        );
        setValue("vialsPerCycle", row.vialsPerCycle ?? "");
        setValue("totalCyclesFormulated", row.totalCyclesFormulated ?? "");
        setValue("totalFormulatedVials", row.totalFormulatedVials ?? "");
        setValue("totalVialsApproved", row.totalVialsApproved ?? "");
        setValue("totalAppliedVials", row.totalAppliedVials ?? "");
        setValue("purchasedVials", row.purchasedVials ?? "");
        setValue("purchasedNotApplied", row.purchasedNotApplied ?? "");
        setValue("unusedVials", row.unusedVials ?? "");
        setValue(
            "importNotAvailable",
            row.importNotAvailable !== null
                ? { label: row.importNotAvailable ? "Sí" : "No", value: row.importNotAvailable ? 1 : 0 }
                : null
        );
        setValue("submissionDate", row.submissionDate ? dayjs(row.submissionDate) : null);
        setValue("autoDate", row.autoDate ? dayjs(row.autoDate) : null);
        setValue("approvalDate", row.approvalDate ? dayjs(row.approvalDate) : null);
        setValue("authorizationNumber", row.authorizationNumber ?? "");
        setValue("awardedTo", row.awardedTo ?? "");
        setValue("startDateTreatment", row.startDateTreatment ? dayjs(row.startDateTreatment) : null);
    };

    const cboHandleChange = (name) => (event) => {
        setValue(name, event);
    };

    const formatDateOrNull = (value) =>
        value && dayjs(value).isValid()
            ? dayjs(value).format("YYYY-MM-DDTHH:mm:ss")
            : null;

    const onSubmit = async (d) => {

        const obj = {
            ...rowObj,
            id: rowObj?.id ?? 0,
            prescriptionId: prescriptionId,
            cie10_11: d.cie10_11?.value,
            cie10OtherDescription: d.cie10OtherDescription,
            relapseDisease: d.relapseDisease?.value === 1,
            vialsPerCycle: d.vialsPerCycle,
            totalCyclesFormulated: d.totalCyclesFormulated,
            totalFormulatedVials: d.totalFormulatedVials,
            totalVialsApproved: d.totalVialsApproved,
            totalAppliedVials: d.totalAppliedVials,
            purchasedVials: d.purchasedVials,
            purchasedNotApplied: d.purchasedNotApplied,
            unusedVials: d.unusedVials,
            importNotAvailable: d.importNotAvailable === null ? null : d.importNotAvailable.value === 1,
            submissionDate: formatDateOrNull(d.submissionDate),
            autoDate: formatDateOrNull(d.autoDate),
            approvalDate: formatDateOrNull(d.approvalDate),
            authorizationNumber: d.authorizationNumber,
            awardedTo: d.awardedTo,
            startDateTreatment: formatDateOrNull(d.startDateTreatment)
        };
        await handleCreateUpdate(obj);
    };

    const disabled = !!readOnly;

    return (
        <Dialog fullWidth maxWidth="md" open={openCRU} onClose={handleRequestClose}>
            <DialogTitle>
                <IntlMessages id={!rowObj ? "core.add" : "core.edit"} />
            </DialogTitle>
            <DialogContent>
                <FormProvider {...methods}>
                    <form noValidate autoComplete="off">
                        <Grid
                            container
                            spacing={2}
                            sx={{ '& .MuiFormControl-root': { width: '100%' }, '& .MuiInputBase-root': { height: 40 } }}
                        >
                            
                            <Grid item xs={12} md={6}>
                                <FormSelectAutoComplete
                                    name="cie10_11"
                                    label={<IntlMessages id="sylvant.cie10" />}
                                    options={DiagnosticCodeCIEList}
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormSelectAutoComplete
                                    name="relapseDisease"
                                    label={<IntlMessages id="qarziba.relapseDisease" />}
                                    options={boolList}
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="cie10OtherDescription"
                                    label={<IntlMessages id="core.other" />}
                                    disabled={disabled || !(cie10_11Value && cie10_11Value.value === 16)}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="vialsPerCycle"
                                    label={<IntlMessages id="qarziba.vialsPerCycle" />}
                                    type="number"
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="totalCyclesFormulated"
                                    label={<IntlMessages id="qarziba.totalCyclesFormulated" />}
                                    type="number"
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="totalFormulatedVials"
                                    label={<IntlMessages id="qarziba.totalFormulatedVials" />}
                                    type="number"
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="totalVialsApproved"
                                    label={<IntlMessages id="qarziba.totalVialsApproved" />}
                                    type="number"
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="totalAppliedVials"
                                    label={<IntlMessages id="qarziba.totalAppliedVials" />}
                                    type="number"
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="purchasedVials"
                                    label={<IntlMessages id="qarziba.purchasedVials" />}
                                    type="number"
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="purchasedNotApplied"
                                    label={<IntlMessages id="qarziba.purchasedNotApplied" />}
                                    type="number"
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="unusedVials"
                                    label={<IntlMessages id="qarziba.unusedVials" />}
                                    type="number"
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormSelectAutoComplete
                                    name="importNotAvailable"
                                    label={<IntlMessages id="vitalNotAvailable.importNotAvailable" />}
                                    options={boolList}
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormDatePicker
                                    name="submissionDate"
                                    label={<IntlMessages id="vitalNotAvailable.submissionDate" />}
                                    format="YYYY-MM-DD"
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormDatePicker
                                    name="autoDate"
                                    label={<IntlMessages id="vitalNotAvailable.autoDate" />}
                                    format="YYYY-MM-DD"
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormDatePicker
                                    name="approvalDate"
                                    label={<IntlMessages id="vitalNotAvailable.approvalDate" />}
                                    format="YYYY-MM-DD"
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="authorizationNumber"
                                    label={<IntlMessages id="vitalNotAvailable.authorizationNumber" />}
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="awardedTo"
                                    label={<IntlMessages id="vitalNotAvailable.awardedTo" />}
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormDatePicker
                                    name="startDateTreatment"
                                    label={<IntlMessages id="vitalNotAvailable.startDateTreatment" />}
                                    format="YYYY-MM-DD"
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                        </Grid>
                    </form>
                </FormProvider>
                {!disabled && (
                    <div className="text-center mt-4">
                        <ButtonAsync
                            onClick={handleSubmit(onSubmit)}
                            variant="contained"
                            color="primary"
                        >
                            <IntlMessages id="core.save" />
                        </ButtonAsync>
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleRequestClose} color="secondary">
                    <IntlMessages id="core.close" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default QarzibaForm;
